import { USER_TYPES } from '@medflex/utils/constants';
import { get as getFullNameWithTitle } from '@medflex/utils/getFullNameWithTitle';
import { TRACKING_ACTIONS, TRACKING_ELEMENT_TYPES, trackEvent } from '@medflex/utils/Tracking';
import config from '@medflex/utils/config';
import isSelf from '@medflex/utils/isSelf';
import isDemoPatient from '@medflex/utils/isDemoPatient';

import makeApiRequest, { getCancelToken } from '../../../api/makeApiRequest';

let cancelToken = null;

const mapSuperUser = (dummy, superuser) => ({
  ...dummy,
  attributes: {
    ...dummy.attributes,
    physician: { ...dummy.attributes.physician, ...superuser.attributes },
  },
});

const normalizeContact = ({ id, attributes }) => {
  const type = attributes.physician ? USER_TYPES.PHYSICIAN : USER_TYPES.PATIENT;
  const { [type]: user, messages, state, ...props } = attributes;
  const enabled = state === 'paired';

  const isMedflexSupport = user.id === config.MEDFLEX_SUPPORT_PATIENT_ID;

  return {
    id,
    ...props,
    messages,
    enabled,
    user: {
      ...user,
      fullName: `${user.firstName} ${user.lastName}`,
      initials: `${user.firstName[0]}${user.lastName[0]}`,
      fullNameWithTitle: getFullNameWithTitle(user),
    },
    type,
    _dateSort: isMedflexSupport ? 9999999999999 : new Date(messages.lastAt).getTime(),
    _nameSort: isMedflexSupport ? 'A' : user.lastName.toUpperCase(),
  };
};

const mergeContacts = (superuser, currentContacts = [], contacts = []) => {
  const total = contacts.length;
  if (!total) return currentContacts;
  const contactsUpdated = [...currentContacts];
  const newContacts = [];
  contacts.forEach(contact => {
    let contactNormalized;
    const isSelfContact = isSelf(contact.attributes.physician || contact.attributes.patient);
    if (isSelfContact && superuser.type !== USER_TYPES.PATIENT) {
      const contactMapped = mapSuperUser(contact, superuser);
      contactNormalized = normalizeContact(contactMapped);
    } else {
      contactNormalized = normalizeContact(contact);

      const index = currentContacts.findIndex(c => c.id === contact.id);
      if (index === -1) {
        newContacts.push(contactNormalized);
      } else {
        contactsUpdated[index] = contactNormalized;
      }
    }
  });

  const contactsMerged = newContacts.concat(contactsUpdated);
  return contactsMerged;
};

const chatContacts = {
  name: 'chatContacts',
  state: [],
  reducers: {
    onLoaded: (state, payload, superuser) => {
      const { data } = payload;
      if (data.length === 0) return state;

      if (superuser.type === USER_TYPES.PATIENT) {
        return data
          .filter(user => !isSelf(user.attributes.physician || user.attributes.patient))
          .map(normalizeContact);
      }

      if (data.find(user => user.attributes.patient && isDemoPatient(user.attributes.patient))) {
        localStorage.setItem('demoPatientExists', 'true');
      }
      return data.map(user =>
        isSelf(user.attributes.physician || {})
          ? normalizeContact(mapSuperUser(user, superuser))
          : normalizeContact(user),
      );
    },
    onLoadedSelectedContact: (state, payload) => {
      const { data } = payload;
      if (!data) return state;

      const chats = state.filter(c => c.id !== data.id);
      return [...chats, normalizeContact(data)];
    },
    onAddContact: (state, contact) => {
      const newContact = normalizeContact(contact);
      return state.reduce(
        (prev, c) => {
          if (c.id === newContact.id) {
            return prev;
          }

          return [...prev, c];
        },
        [newContact],
      );
    },
    '_chat/onMessage': (state, event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'MESSAGE_CREATED') {
        const { payload } = data;
        const { contactId, id, recipient } = payload;
        if (state.some(contact => contact.id === contactId)) {
          return state.map(contact => {
            if (contact.id === contactId) {
              // new message unarchives a contact
              const { archivedAt, ...rest } = contact;

              // TODO: remove when backend handles last read message for sender
              const mainContact = contact.mainContactId
                ? state.find(c => c.id === contact.mainContactId)
                : null;
              const userId =
                mainContact && mainContact.type === 'physician'
                  ? mainContact.user.id
                  : contact.user.id;
              if (userId === recipient) {
                return {
                  ...rest,
                  _dateSort: new Date().getTime(),
                  messages: {
                    ...contact.messages,
                    lastReadId: id,
                    // set last message date if not existent yet to display contact
                    // in PhysicianChatContactList
                    lastAt: contact.messages.lastAt || new Date().toISOString(),
                    unread: 0,
                  },
                };
              }
              // END TODO

              return {
                ...rest,
                _dateSort: new Date().getTime(),
                messages: {
                  ...contact.messages,
                  unread: contact.messages.unread + 1,
                  // set last message date if not existent yet to display
                  // contact in PhysicianChatContactList
                  lastAt: contact.messages.lastAt || new Date().toISOString(),
                },
              };
            }
            return contact;
          });
        }
      }

      if (data.type === 'CONTACT_DELETED') {
        const { payload } = data;
        const deletedIds = payload.map(({ id }) => id);

        return state.filter(
          contact =>
            !deletedIds.includes(contact.id) && !deletedIds.includes(contact.mainContactId),
        );
      }

      if (data.type === 'MESSAGE_MULTICAST_CREATED') {
        const { payload } = data;
        return state.map(contact => {
          const contactMessage = payload.find(({ contactId }) => contactId === contact.id);

          if (!contactMessage) return contact;

          const mainContact = contact.mainContactId
            ? state.find(c => c.id === contact.mainContactId)
            : contact;

          return {
            ...mainContact,
            _dateSort: new Date().getTime(),
            messages: {
              ...mainContact.messages,
            },
          };
        });
      }
      return state;
    },
    // save lastSeenMessageTimestamp
    onMessageSeen: (state, { id, lastSeenMessageTimestamp }) =>
      state.map(contact => {
        if (contact.id === id) {
          return {
            ...contact,
            user: {
              ...contact.user,
              lastSeenMessageTimestamp,
            },
            messages: {
              ...contact.messages,
            },
          };
        }
        return contact;
      }),
    onContactsModified: (state, contacts, superuser) => {
      const contactsMerged = mergeContacts(superuser, state, contacts);
      return contactsMerged;
    },
  },
  messageListeners: {
    CONTACT_CREATED({ payload }) {
      this.onAddContact(payload);
    },
    CONTACT_LAST_SEEN_MSG({ payload }) {
      this.onMessageSeen(payload);
    },
    CONTACTS_MODIFIED({ payload, state }) {
      this.onContactsModified(payload, state.superuser);
    },
    SESSION_CREATED() {
      this.refresh();
    },
    SESSION_SCHEDULE_CANCELED() {
      this.refresh();
    },
  },
  effects(dispatch) {
    return {
      load(_, { superuser, oidcSession }) {
        const cToken = getCancelToken();
        if (!cancelToken) {
          cancelToken = cToken;
        } else {
          cancelToken.cancel();
        }

        const requestConfig = {
          url: '/contacts',
          cancelToken: cToken.token,
        };

        /** @type any */
        const [request] = makeApiRequest(requestConfig, oidcSession);
        return request
          .then(({ error, data }) => {
            if (error) throw error;
            dispatch.errors.remove({ realm: 'chat_contacts', type: 'load' });
            // @todo: remove when onlineStatus has own endpoint. The following
            // prevents unnecessary contact fetch on page load
            dispatch.onlineStatus.onLoaded(data);
            return this.onLoaded(
              {
                data: data.data,
              },
              superuser,
            );
          })
          .catch(error => {
            dispatch.errors.add({ realm: 'chat_contacts', type: 'load', error });
          });
      },
      async refresh(_, { superuser, oidcSession }) {
        const url = '/contacts';
        const [request] = makeApiRequest({ url }, oidcSession);
        return request
          .then(({ error, data }) => {
            if (error) throw error;
            dispatch.errors.remove({ realm: 'chat_contacts', type: 'load' });
            this.onLoaded(data, superuser);
          })
          .catch(error => {
            dispatch.errors.add({ realm: 'chat_contacts', type: 'load', error });
          });
      },
      acceptSharedContact({ messageId, patient }, { oidcSession }) {
        const url = '/contacts';
        const body = {
          data: {
            type: 'contacts',
            attributes: {
              messageId,
              patient: {
                ...patient,
                source: 'sharedContact',
              },
            },
          },
        };
        const [request] = makeApiRequest({ url, method: 'post', data: body }, oidcSession);

        return request
          .then(({ error, data, headers }) => {
            if (error) throw error;
            dispatch.errors.remove({ realm: 'chat_contacts', type: 'acceptSharedContact' });
            return { data, headers };
          })
          .catch(error => {
            dispatch.errors.add({ realm: 'chat_contacts', type: 'acceptSharedContact', error });
            throw error;
          });
      },
      async createSubContact({ mainContactId, firstName, lastName, dateOfBirth }, { oidcSession }) {
        const url = '/contacts';
        const body = {
          data: {
            type: 'contacts',
            attributes: {
              mainContactId,
              patient: {
                firstName,
                lastName,
                dateOfBirth,
              },
            },
          },
        };
        /** @type any */
        const [request] = makeApiRequest({ url, method: 'post', data: body }, oidcSession);
        const { error } = await request;
        if (error) throw error;
      },
      async delete({ contactId }, { oidcSession }) {
        dispatch.errors.remove({ realm: 'chat_contacts', type: 'delete' });
        const url = `contacts/${contactId}`;

        const [request] = makeApiRequest({ url, method: 'delete' }, oidcSession);
        const { error, data } = await request;

        if (!error) {
          // no reducer call, because it is handled by web socket message
          trackEvent({
            zone: 'Contacts',
            action: TRACKING_ACTIONS.CLICK,
            elementType: TRACKING_ELEMENT_TYPES.BUTTON,
            actionText: 'Delete successful',
          });
        } else {
          trackEvent({
            zone: 'Contacts',
            action: TRACKING_ACTIONS.CLICK,
            elementType: TRACKING_ELEMENT_TYPES.BUTTON,
            actionText: 'Delete failed',
          });
          dispatch.errors.add({
            realm: 'chat_contacts',
            type: 'delete',
            error: data.errors ? data.errors[0] : error,
          });
          throw error;
        }
      },
      async archive({ contactId }, { oidcSession }) {
        const url = `contacts/${contactId}`;

        const body = {
          data: {
            type: 'contacts',
            id: contactId,
            attributes: {
              archived: true,
            },
          },
        };

        const [request] = makeApiRequest({ url, method: 'patch', data: body }, oidcSession);
        const { error } = await request;

        if (!error) {
          trackEvent({
            zone: 'Contacts',
            action: TRACKING_ACTIONS.CLICK,
            elementType: TRACKING_ELEMENT_TYPES.BUTTON,
            actionText: 'Archvie successful',
          });
        } else {
          trackEvent({
            zone: 'Contacts',
            action: TRACKING_ACTIONS.CLICK,
            elementType: TRACKING_ELEMENT_TYPES.BUTTON,
            actionText: 'Archvie failed',
          });
          throw error;
        }
      },
      async updatePatientData({ contactId, payload }, { oidcSession }) {
        const url = `contacts/${contactId}`;

        const body = {
          data: {
            type: 'contacts',
            id: contactId,
            attributes: { ...payload },
          },
        };

        const [request] = makeApiRequest({ url, method: 'patch', data: body }, oidcSession);
        const { error } = await request;

        if (!error) {
          trackEvent({
            zone: 'Contacts',
            action: TRACKING_ACTIONS.SUBMIT,
            elementType: TRACKING_ELEMENT_TYPES.FORM,
            actionText: 'Change Patient Data success',
          });
        } else {
          trackEvent({
            zone: 'Contacts',
            action: TRACKING_ACTIONS.SUBMIT,
            elementType: TRACKING_ELEMENT_TYPES.FORM,
            actionText: 'Change Patient Data falure',
          });
          throw error;
        }
      },
      async dearchive({ contactId }, { oidcSession }) {
        const url = `contacts/${contactId}`;

        const body = {
          data: {
            type: 'contacts',
            id: contactId,
            attributes: {
              archived: false,
            },
          },
        };

        const [request] = makeApiRequest({ url, method: 'patch', data: body }, oidcSession);
        const { error } = await request;

        if (!error) {
          trackEvent({
            zone: 'Contacts',
            action: TRACKING_ACTIONS.CLICK,
            elementType: TRACKING_ELEMENT_TYPES.BUTTON,
            actionText: 'Dearchive successful',
          });
        } else {
          trackEvent({
            zone: 'Contacts',
            action: TRACKING_ACTIONS.CLICK,
            elementType: TRACKING_ELEMENT_TYPES.BUTTON,
            actionText: 'Dearchive failed',
          });
          throw error;
        }
      },
      async setLastReadMessage({ contactId, id }, { oidcSession }) {
        const url = `contacts/${contactId}/relationships/last-read-message`;

        const body = {
          // null => first message is unread
          data: id ? { type: 'message', id } : null,
        };

        /** @type any */
        const [request] = makeApiRequest({ url, method: 'patch', data: body }, oidcSession);
        const { error } = await request;
        if (!error) return this.refresh();
        throw error;
      },
      async connectOrgaPhysician(userId, { oidcSession }) {
        dispatch.errors.remove({ realm: 'chat_contacts', type: 'connect_orga_physician' });
        const url = 'contacts';
        const body = {
          data: {
            type: 'physician-organization-contacts',
            attributes: {
              physicianUserId: userId,
              source: 'addressBook',
            },
          },
        };
        const [request] = makeApiRequest({ url, method: 'post', data: body }, oidcSession);
        const { error } = await request;
        if (error) {
          dispatch.errors.add({ realm: 'chat_contacts', type: 'connect_orga_physician', error });
        }
      },
      async loadSelectedContact(contactId, { superuser, oidcSession }) {
        const url = `contacts/${contactId}`;
        const [request] = makeApiRequest({ url }, oidcSession);
        return request
          .then(({ error, data }) => {
            if (error) throw error;
            dispatch.errors.remove({ realm: 'chat_contacts', type: 'loadSelectedContact' });
            return this.onLoadedSelectedContact(
              {
                data: data.data,
              },
              superuser,
            );
          })
          .catch(error => {
            dispatch.errors.add({
              realm: 'chat_contacts',
              type: 'loadSelectedContact',
              error,
            });
          });
      },
    };
  },
};

export default chatContacts;
